import { type DialogProps } from "../../misc/DialogProps";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { type ReactNode, useRef } from "react";
import { Tab, useRefresh } from "react-admin";
import { DateTime } from "luxon";
import { Tabs } from "../../misc/Tabs";
import { type DateClickArg } from "@fullcalendar/interaction";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { CreateExamForm } from "./CreateExamForm";
import { CreateDrivingLessonForm } from "./CreateDrivingLessonForm";
import { CreateOtherEventForm } from "./CreateOtherEventForm";
import { grants } from "../../backoffice.access_control";
import { type Instructor } from "../../providers/instructorsProvider";
import { type Student } from "../../providers/studentsProvider";
import { isSimulator, type Vehicle } from "../../providers/vehiclesProvider";
import { CreateTheoryLessonForm } from "./CreateTheoryLessonForm";
import { CreateSimulatorDrivingLessonForm } from "./CreateSimulatorDrivingLessonForm";
import { CreateVehicleEventForm } from "./CreateVehicleEventForm";
import { useDraggableDialog } from "../../hooks/useDraggableDialog";

interface AddAppointmentDialogProps extends DialogProps {
  courseId?: string;
  student?: Student;
  instructor?: Instructor;
  vehicle?: Vehicle;
  tabs: Array<"theoryLesson" | "drivingLesson" | "simulatorDrivingLesson" | "exam" | "otherEvent" | "vehicleEvent">;
}

interface AddAppointmentDialogType {
  (props: AddAppointmentDialogProps): ReactNode;
  initialDateTime?: DateTime;
  fullCalendarDateClickHandler: (openDialog: () => void) => (arg: DateClickArg) => void;
}

function _AddAppointmentDialog(props: AddAppointmentDialogProps) {
  const { open, onClose, courseId, instructor, student, vehicle, tabs } = props;
  const refresh = useRefresh();
  const { dialogOffset, onTitleMouseDown } = useDraggableDialog({ open });

  const initialDateTimeRef = useRef(AddAppointmentDialog.initialDateTime);
  if (open && AddAppointmentDialog.initialDateTime) {
    initialDateTimeRef.current = AddAppointmentDialog.initialDateTime;
    // Side effect: reset AddAppointmentDialog.initialDateTime ...
    AddAppointmentDialog.initialDateTime = undefined;
  }
  const initialDateTime = initialDateTimeRef.current;

  const onSuccess = () => {
    onClose();
    refresh();
  };

  const effectiveTabs = tabs.filter((tab) => {
    if (tab === "exam") {
      return grants.has("Exam:create");
    } else if (tab === "theoryLesson") {
      return grants.has("TheoryLesson:create");
    } else {
      return true;
    }
  });
  let dialogTitle: ReactNode = "Neuer Termin";
  if (effectiveTabs.length === 1) {
    if (effectiveTabs[0] === "theoryLesson") {
      dialogTitle = "Neue Theoriestunde";
    } else if (effectiveTabs[0] === "drivingLesson") {
      dialogTitle = "Neue Fahrstunde";
    } else if (effectiveTabs[0] === "simulatorDrivingLesson") {
      dialogTitle = "Neue Simulator-Fahrstunde";
      if (vehicle) {
        dialogTitle = (
          <>
            {dialogTitle}
            <br />
            {`für ${vehicle.name}`}
          </>
        );
      } else if (student) {
        dialogTitle = (
          <>
            {dialogTitle}
            <br />
            {`für ${student.name}`}
          </>
        );
      }
    } else if (effectiveTabs[0] === "exam") {
      dialogTitle = "Neue Prüfung";
    } else if (effectiveTabs[0] === "vehicleEvent") {
      dialogTitle = "Neuer Fahrzeugtermin";
    }
  }

  const drivingLessonForm = (
    <CreateDrivingLessonForm
      initialDateTime={initialDateTime}
      instructor={instructor}
      vehicles={vehicle && !isSimulator(vehicle) ? [vehicle] : undefined}
      onSuccess={onSuccess}
    />
  );

  const simulatorDrivingLessonForm = (
    <CreateSimulatorDrivingLessonForm
      initialDateTime={initialDateTime}
      student={student}
      simulator={isSimulator(vehicle) ? vehicle : undefined}
      onSuccess={onSuccess}
    />
  );

  const theoryLessonForm = (
    <CreateTheoryLessonForm courseId={courseId} initialDateTime={initialDateTime} onSuccess={onSuccess} />
  );

  const examForm = <CreateExamForm initialDateTime={initialDateTime} instructor={instructor} onSuccess={onSuccess} />;

  const otherEventForm = (
    <CreateOtherEventForm instructor={instructor!} initialDateTime={initialDateTime} onSuccess={onSuccess} />
  );

  const vehicleEventForm = vehicle?.id ? (
    <CreateVehicleEventForm vehicleId={vehicle.id} initialDateTime={initialDateTime} onSuccess={onSuccess} />
  ) : null;

  let dialogContent: React.ReactNode = null;
  if (effectiveTabs.length === 1) {
    if (effectiveTabs[0] === "theoryLesson") {
      dialogContent = theoryLessonForm;
    } else if (effectiveTabs[0] === "drivingLesson") {
      dialogContent = drivingLessonForm;
    } else if (effectiveTabs[0] === "simulatorDrivingLesson") {
      dialogContent = simulatorDrivingLessonForm;
    } else if (effectiveTabs[0] === "exam") {
      dialogContent = examForm;
    } else if (effectiveTabs[0] === "otherEvent" && instructor) {
      dialogContent = otherEventForm;
    } else if (effectiveTabs[0] === "vehicleEvent") {
      dialogContent = vehicleEventForm;
    }
  } else if (effectiveTabs.length > 1) {
    dialogContent = (
      <Tabs sx={{ m: 0 }} syncWithLocation={false}>
        {effectiveTabs.map((tab) =>
          tab === "drivingLesson" ? (
            <Tab key="drivingLesson" label="Fahrstunde">
              {drivingLessonForm}
            </Tab>
          ) : tab === "simulatorDrivingLesson" ? (
            <Tab key="simulatorDrivingLesson" label="Simulator-Fahrstunde">
              {simulatorDrivingLessonForm}
            </Tab>
          ) : tab === "exam" ? (
            <Tab key="exam" label="Prüfung">
              {examForm}
            </Tab>
          ) : tab === "theoryLesson" ? (
            <Tab key="theoryLesson" label="Theoriestunde">
              {theoryLessonForm}
            </Tab>
          ) : tab === "otherEvent" && instructor ? (
            <Tab key="otherEvent" label="Sonstiges">
              {otherEventForm}
            </Tab>
          ) : tab === "vehicleEvent" ? (
            <Tab key="vehicleEvent" label="Fahrzeugtermin">
              {vehicleEventForm}
            </Tab>
          ) : null,
        )}
      </Tabs>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      BackdropProps={{
        sx: {
          backgroundColor: "transparent",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "flex-start",
        },
      }}
      PaperProps={{
        sx: {
          transform: `translate(${dialogOffset.x}px, ${dialogOffset.y}px)`,
        },
      }}
    >
      <DialogTitle
        onMouseDown={onTitleMouseDown}
        sx={{
          cursor: "move",
          userSelect: "none",
          marginRight: 0,
          paddingRight: "80px",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        {dialogTitle}
      </DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent sx={{ minWidth: "400px" }}>{dialogContent}</DialogContent>
    </Dialog>
  );
}

export const AddAppointmentDialog: AddAppointmentDialogType = _AddAppointmentDialog as any;
AddAppointmentDialog.fullCalendarDateClickHandler =
  (openDialog: () => void) =>
  ({ dateStr, dayEl, jsEvent }) => {
    try {
      let dateTime = DateTime.fromISO(dateStr, { zone: "Europe/Berlin" });
      // We need to adapt dateTime here, because sometimes FullCalendar does not pass quite the correct date ...
      // Furthermore we add 15 minutes if the click was in the lower half of the 30 min time slot ...
      const { top, height } = dayEl.getBoundingClientRect();
      const h = ((24 - 6) / height) * (jsEvent.clientY - top) + 6;
      const m = (h - Math.floor(h)) * 60;
      if (dateTime.get("minute") === 0) {
        if (h < dateTime.get("hour")) {
          // Do nothing.
        } else if (m >= 25) {
          dateTime = dateTime.plus({ minutes: 30 });
        } else if (m >= 10) {
          dateTime = dateTime.plus({ minutes: 15 });
        }
      } else if (dateTime.get("minute") === 30) {
        if (m >= 55) {
          dateTime = dateTime.plus({ minutes: 30 });
        } else if (m >= 40) {
          dateTime = dateTime.plus({ minutes: 15 });
        }
      } else {
        throw new Error(`Unexpected dateTime.get("minute"): ${dateTime.get("minute")}`);
      }
      AddAppointmentDialog.initialDateTime = dateTime;
      openDialog();
    } catch (error) {
      console.error("Failed to handle dateClick", error);
    }
  };
